import { SETUP_GOALS, RULES_CONSTRAINTS, type SetupGoal } from "connected-components/optimize-experiments/constants"
import { type ConcentrationConstraint, type MaxTradeConstraint } from "connected-components/optimize-experiments/hooks/types"
import type { FactorRuleDisplay } from "connected-components/optimize-experiments/components/factor-rules/types"
import type { SecuritySearchFilter } from "resources/filters"

type StringMaxTradeConstraint<T> = {
  [K in keyof T]: T[K] extends number ? string : T[K]
}

export type MaxTradeConstraintInput = StringMaxTradeConstraint<MaxTradeConstraint>
export type ConcentrationConstraintInput = StringMaxTradeConstraint<ConcentrationConstraint>

export interface SetupSection {
  goal: SetupGoal
  targetTotalRiskValue?: string
  forecast?: string
}

export interface RulesSection {
  tradeSwaps: boolean | null
  maxTurnover: string | null
  concentration: ConcentrationConstraintInput | null
  maxTrade: MaxTradeConstraintInput | null
  equityChange: string | null
  netLong: { longMarketValue: string, shortMarketValue: number } | null
  preservePositionDirection: boolean | null
  exposure: FactorRuleDisplay[] | null
  composition: FactorRuleDisplay[] | null
}

interface UniverseOption {
  filter?: SecuritySearchFilter
  value: string
  count?: number
}

export interface UniverseSection {
  long: UniverseOption | null
  short: UniverseOption | null
}

export interface FormSections {
  setup: SetupSection
  rules: RulesSection
  universe: UniverseSection
}

export interface ConstraintInput {
  fieldKey: string
  unit: string
  label?: string
  disabled?: boolean
}

export interface ConstraintConfig<TValue> {
  label: string
  tooltip?: string
  defaultValue: TValue | null
  inputs?: ConstraintInput[]
}

export const FORM_CONSTRAINTS = {
  [RULES_CONSTRAINTS.MAX_TURNOVER]: {
    label: "Max turnover",
    defaultValue: "20",
    inputs: [
      {
        fieldKey: "maxTurnover",
        unit: "%",
      },
    ],
  } satisfies ConstraintConfig<string>,
  [RULES_CONSTRAINTS.CONCENTRATION]: {
    label: "Concentration",
    defaultValue: {
      minConcentration: "0",
      maxConcentration: "100",
    },
    inputs: [
      {
        fieldKey: "minConcentration",
        unit: "%",
        label: "Min",
      },
      {
        fieldKey: "maxConcentration",
        unit: "%",
        label: "Max",
      },
    ],
  } satisfies ConstraintConfig<ConcentrationConstraintInput>,
  [RULES_CONSTRAINTS.MAX_TRADE]: {
    label: "Max trade",
    defaultValue: {
      percentADV: "25",
      percentEquity: "25",
      percentOriginalEconomicExposure: "25",
    },
    inputs: [
      {
        fieldKey: "percentADV",
        unit: "% ADV",
      },
      {
        fieldKey: "percentEquity",
        unit: "% Eq",
      },
      {
        fieldKey: "percentOriginalEconomicExposure",
        unit: "% Rel",
      },
    ],
  } satisfies ConstraintConfig<MaxTradeConstraintInput>,
  [RULES_CONSTRAINTS.EQUITY_CHANGE]: {
    label: "Equity change",
    defaultValue: "3",
    inputs: [
      {
        fieldKey: "equityChange",
        unit: "% GMV",
      },
    ],
  } satisfies ConstraintConfig<string>,
  [RULES_CONSTRAINTS.NET_LONG]: {
    label: "Net long",
    defaultValue: { longMarketValue: "0", shortMarketValue: 0 },
    inputs: [
      {
        fieldKey: "longMarketValue",
        unit: "% GMV",
        label: "Long",
      },
      {
        fieldKey: "shortMarketValue",
        unit: "% GMV",
        label: "Short",
        disabled: true,
      },
    ],
  } satisfies ConstraintConfig<{ longMarketValue: string, shortMarketValue: number }>,
  [RULES_CONSTRAINTS.TRADE_SWAPS]: {
    label: "Trade baskets",
    tooltip: "This allows baskets to be treated as a security that can be traded, if any are held in this portfolio",
    defaultValue: true,
  } satisfies ConstraintConfig<boolean>,
  [RULES_CONSTRAINTS.PRESERVE_POSITION_DIRECTION]: {
    label: "Preserve position direction",
    tooltip: "Preserve position direction tooltip",
    defaultValue: false,
  } satisfies ConstraintConfig<boolean>,
}

const DEFAULT_SETUP: SetupSection = {
  goal: SETUP_GOALS.MAXIMIZE_SPECIFIC_RISK,
  forecast: "none",
}

export const DEFAULT_UNIVERSE: UniverseSection = {
  long: null,
  short: null,
}

const DEFAULT_RULES: RulesSection = {
  tradeSwaps: null,
  maxTurnover: null,
  concentration: null,
  maxTrade: null,
  equityChange: null,
  netLong: null,
  preservePositionDirection: null,
  exposure: null,
  composition: null,
}

export const getDefaultConfiguration = (): FormSections => {
  return {
    setup: DEFAULT_SETUP,
    rules: DEFAULT_RULES,
    universe: DEFAULT_UNIVERSE,
  }
}
